import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TourWelcome from './TourWelcome';
import ReplyBell from './ReplyBell';
import { useBrand } from '../context/BrandContext';
import { useOrg } from '../context/OrgContext';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Users, 
  MessageSquare, 
  FlaskConical, 
  CheckCircle, 
  Send, 
  Settings,
  Ticket,
  LogOut,
  Zap,
  Plug,
  Menu,
  X,
  BarChart3,
  Target,
  Building2,
  HeartPulse,
  ListChecks,
  FileInput,
  CalendarClock,
  CalendarDays,
  UsersRound,
  Mail,
  Layers,
  BookOpen,
  Workflow,
  ListTodo,
  Inbox,
  SlidersHorizontal,
  Megaphone,
  Rocket,
  Building,
  ArrowRightLeft,
  ShieldCheck,
  BadgeCheck,
  FileText,
  EyeOff,
  Boxes,
  ClipboardCheck,
  Handshake,
  MessageSquareText,
  BadgePoundSterling
} from 'lucide-react';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = () => {
  const { user, logout } = useAuth();
  const { brand } = useBrand();
  const { hq, orgs, activeOrgId, switchOrg } = useOrg();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menuCounts, setMenuCounts] = useState({
    pipelines: 0,
    leads: 0,
    pending_approvals: 0,
    sent: 0,
    website_leads: 0,
    companies: 0,
    contacts: 0,
    pending_contacts: 0
  });
  const [dailySends, setDailySends] = useState({
    sent_today: 0,
    daily_cap: 20,
    percentage: 0
  });

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const response = await axios.get(`${API}/stats/menu-counts`);
        setMenuCounts(response.data);
      } catch (error) {
        console.error('Failed to fetch menu counts:', error);
      }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  useEffect(() => {
    const fetchDailySends = async () => {
      try {
        const response = await axios.get(`${API}/settings/daily-sends`);
        setDailySends(response.data);
      } catch (error) {
        console.error('Failed to fetch daily sends:', error);
      }
    };
    fetchDailySends();
    const interval = setInterval(fetchDailySends, 30000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  const navItems = [
    { section: 'Overview', items: [
      { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    ]},
    { section: 'Lead Generation', items: [
      { path: '/admin/care-directory', icon: HeartPulse, label: 'Care Directory' },
      { path: '/admin/market-intel', icon: BarChart3, label: 'Market Intelligence' },
      { path: '/admin/funnel', icon: Inbox, label: 'Funnel Inbox' },
      { path: '/admin/verification', icon: ShieldCheck, label: 'Verification & Assurance' },
      { path: '/admin/listing-proposals', icon: FileText, label: 'Listing Proposals' },
      { path: '/admin/listing-approvals', icon: ClipboardCheck, label: 'Listing Approvals' },
      { path: '/admin/discount-codes', icon: Ticket, label: 'Discount Codes' },
      { path: '/admin/review-register', icon: ClipboardCheck, label: 'Review Register' },
      { path: '/admin/supplier-editor', icon: Boxes, label: 'Supplier Editor' },
      { path: '/admin/product-reviews', icon: MessageSquareText, label: 'Product Reviews' },
      { path: '/admin/supplier-invites', icon: Send, label: 'Supplier Invites' },
      { path: '/admin/question-sets', icon: FileText, label: 'Question Sets' },
      { path: '/admin/governance', icon: EyeOff, label: 'Field Governance' },
      { path: '/admin/procurement-reports', icon: FileText, label: 'Procurement Reports' },
      { path: '/admin/fulfilment', icon: Boxes, label: 'Fulfilment' },
      { path: '/admin/subscriptions', icon: BadgePoundSterling, label: 'Subscriptions' },
      { path: '/admin/partners', icon: Handshake, label: 'Partners' },
    ]},
    { section: 'Pipeline', items: [
      { path: '/admin/companies', icon: Building2, label: 'Companies', badge: { count: menuCounts.companies, type: 'active' } },
      { path: '/admin/contacts', icon: Users, label: 'Contacts', badge: { count: menuCounts.contacts, type: 'hot' } },
      { path: '/admin/lists', icon: ListChecks, label: 'Lists' },
      { path: '/admin/forms', icon: FileInput, label: 'Forms' },
      { path: '/admin/meetings', icon: CalendarClock, label: 'Meetings' },
      { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    ]},
    { section: 'Engagement', items: [
      { path: '/admin/inbox', icon: Inbox, label: 'Inbox' },
      { path: '/admin/automations', icon: Workflow, label: 'Automations' },
      { path: '/admin/campaigns', icon: Mail, label: 'Campaigns' },
      { path: '/admin/bulk-email', icon: Send, label: 'Bulk Email' },
    ]},
    { section: 'Settings', items: [
      { path: '/admin/custom-properties', icon: SlidersHorizontal, label: 'Custom Properties' },
      { path: '/admin/guide', icon: BookOpen, label: 'User Guide' },
      { path: '/admin/settings', icon: Settings, label: 'Settings', badge: { count: menuCounts.pending_contacts, type: 'pending' } },
    ]},
  ];

  // Suppliers (sub-account magic-link logins) are locked to their own scope and
  // only see self-service CRM tools - no lead-gen, admin or agency surfaces.
  const isSupplier = String(user?.role).toLowerCase() === 'supplier';
  const SUPPLIER_PATHS = new Set([
    '/admin', '/admin/care-directory', '/admin/companies', '/admin/contacts', '/admin/lists', '/admin/deals', '/admin/tasks',
    '/admin/analytics', '/admin/inbox', '/admin/automations', '/admin/replies', '/admin/sent', '/admin/campaigns', '/admin/guide',
  ]);
  const visibleNav = isSupplier
    ? navItems
        .map((s) => ({ ...s, items: s.items.filter((i) => SUPPLIER_PATHS.has(i.path)) }))
        .filter((s) => s.items.length > 0)
    : navItems;
  const getBadgeStyle = (type) => {
    switch (type) {
      case 'hot': return 'bg-red-100 text-red-700';
      case 'active': return 'bg-emerald-100 text-emerald-700';
      case 'warning': return 'bg-amber-100 text-amber-700';
      case 'pending': return 'bg-blue-100 text-blue-700';
      case 'new': return 'bg-violet-100 text-violet-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-4 lg:p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={brand.logo_url} alt={brand.brand_name} className="w-9 h-9 lg:w-10 lg:h-10 object-contain" />
            <div>
              <h1 className="text-base lg:text-lg font-bold text-gray-900 tracking-tight leading-tight">{brand.brand_name}</h1>
              <p className="text-[10px] lg:text-xs font-medium text-gray-500 uppercase tracking-wider">CRM</p>
            </div>
          </div>
          {/* Close button on mobile */}
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            data-testid="close-sidebar-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Account switcher (agency sub-accounts) */}
      {orgs.length > 0 && !isSupplier && (
        <div className="px-4 lg:px-6 py-3 border-b border-gray-200">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5 flex items-center gap-1">
            <ArrowRightLeft className="w-3 h-3" /> Account
          </p>
          <Select value={activeOrgId || 'hq'} onValueChange={(v) => switchOrg(v === 'hq' ? null : v)}>
            <SelectTrigger className="h-9 bg-gray-50 border-gray-200 text-sm" data-testid="account-switcher">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hq">{hq?.name || 'HQ'} (HQ)</SelectItem>
              {orgs.map((o) => (
                <SelectItem key={o.id} value={o.id} data-testid={`switcher-org-${o.id}`}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 lg:py-4 lg:px-3">
        {visibleNav.map((section) => (
          <div key={section.section} className="mb-4 lg:mb-6">
            <p className="px-3 mb-1.5 lg:mb-2 text-[10px] lg:text-xs font-semibold uppercase tracking-wider text-gray-400">
              {section.section}
            </p>
            {section.items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/admin'}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) => `
                  flex items-center gap-2.5 lg:gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-xl mb-0.5 lg:mb-1 transition-all text-sm lg:text-base
                  ${isActive 
                    ? 'bg-blue-50 text-blue-700 font-medium border-l-3 border-blue-600' 
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}
                `}
              >
                <item.icon className="w-4 h-4 lg:w-5 lg:h-5 shrink-0" />
                <span className="flex-1 truncate">{item.label}</span>
                {item.badge && (item.badge.count > 0 || item.badge.type === 'new') && (
                  <span className={`px-1.5 lg:px-2 py-0.5 rounded-full text-[10px] lg:text-xs font-semibold ${getBadgeStyle(item.badge.type)}`}>
                    {item.badge.count || 'NEW'}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-3 lg:p-4 border-t border-gray-200">
        <div className="mb-3 lg:mb-4 p-2.5 lg:p-3 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-1.5 lg:mb-2">
            <span className="text-[10px] lg:text-xs font-semibold text-gray-500 uppercase">Daily Sends</span>
            <span className="text-[10px] lg:text-xs font-semibold text-blue-600" data-testid="daily-sends-count">
              {dailySends.sent_today}/{dailySends.daily_cap}
            </span>
          </div>
          <Progress value={dailySends.percentage} className="h-1.5 lg:h-2" />
        </div>

        <div className="flex items-center gap-2 lg:gap-3">
          <Avatar className="h-8 w-8 lg:h-10 lg:w-10 border-2 border-gray-200">
            <AvatarFallback className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white text-sm lg:text-base font-semibold">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm lg:text-base font-semibold text-gray-900 truncate">{user?.name}</p>
            <p className="text-xs lg:text-sm text-gray-500 truncate">{user?.company || user?.email}</p>
          </div>
          <button 
            onClick={logout}
            data-testid="logout-btn"
            className="p-1.5 lg:p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4 lg:w-5 lg:h-5" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Mobile header bar */}
      <div className="fixed top-0 left-0 right-0 z-40 lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            data-testid="open-sidebar-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-1.5">
            <img src={brand.logo_url} alt={brand.brand_name} className="w-7 h-7 object-contain" />
            <span className="text-base font-bold text-gray-900">{brand.brand_name}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ReplyBell testid="reply-bell-mobile" />
          <span className="text-xs text-blue-600 font-semibold">{dailySends.sent_today}/{dailySends.daily_cap}</span>
          <Avatar className="h-7 w-7 border border-gray-200">
            <AvatarFallback className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white text-xs font-semibold">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
        </div>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - fixed on desktop, slide-in drawer on mobile */}
      <aside className={`
        fixed h-full z-50 bg-white border-r border-gray-200 flex flex-col shadow-sm
        w-[280px] lg:w-64
        transition-transform duration-200 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <div className="hidden lg:block fixed top-4 right-6 z-30"><ReplyBell /></div>
      <main className="flex-1 min-w-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-4 lg:p-8 max-w-full overflow-x-hidden">
          <Outlet />
        </div>
      </main>
      <TourWelcome user={user} />
    </div>
  );
};

export default Layout;
